import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { handleGrantAccess } from "../api/accessRequest.api";
import Card from "../components/shared/card";
import ThemedSpinner from "../components/shared/spinner";
import Typography from "../components/shared/typography";

const AccessRequestGrantPage = () => {
  const { accessGrantJwt } = useParams<{ accessGrantJwt: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const grantAccess = async () => {
      if (!accessGrantJwt) {
        setError("Invalid access request link");
        setIsLoading(false);
        return;
      }

      try {
        await handleGrantAccess(accessGrantJwt);
        setIsSuccess(true);
      } catch (err) {
        console.error("Error granting access:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Failed to grant access. Please try again."
        );
      } finally {
        setIsLoading(false);
      }
    };

    grantAccess();
  }, [accessGrantJwt]);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-md mx-auto">
          <Card variant="cream" elevation={2}>
            <Card.Body className="text-center py-8">
              <ThemedSpinner size="lg" />
              <Typography variant="h5" className="mt-4 text-primary">
                Processing Access Request...
              </Typography>
              <Typography variant="body2" className="mt-2 text-gray-600">
                Please wait while we grant access to your account.
              </Typography>
            </Card.Body>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-md mx-auto">
        <Card variant="cream" elevation={2}>
          <Card.Body className="text-center py-8">
            {isSuccess ? (
              <>
                <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <Typography variant="h4" className="text-green-600 mb-2">
                  Access Granted!
                </Typography>
                <Typography variant="body1" className="text-gray-600 mb-4">
                  Your access request has been successfully granted. You can now
                  access the requested resources.
                </Typography>
                <a
                  href="/"
                  className="inline-block px-6 py-2 bg-terracotta text-white rounded-lg hover:bg-terracotta-700 transition-colors"
                >
                  Go to Home
                </a>
              </>
            ) : (
              <>
                <XCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <Typography variant="h4" className="text-red-600 mb-2">
                  Access Denied
                </Typography>
                <Typography variant="body1" className="text-gray-600 mb-4">
                  {error ||
                    "There was an error processing your access request."}
                </Typography>
                <a
                  href="/"
                  className="inline-block px-6 py-2 bg-terracotta text-white rounded-lg hover:bg-terracotta-700 transition-colors"
                >
                  Go to Home
                </a>
              </>
            )}
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default AccessRequestGrantPage;
